import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const DEFAULT_SETTINGS = {
  price: 5000,
  durationDays: 7,
  maxSlots: 5,
  note:
    "Featured sponsored shops appear first on the luxury landing page and get premium placement for discovery.",
};

const fmt = (value) =>
  `LKR ${Number(value || 0).toLocaleString("en-LK", {
    maximumFractionDigits: 0,
  })}`;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  const ms = toMillis(value);
  return ms ? new Date(ms).toLocaleString() : "Not set";
}

function isActiveSponsor(row = {}) {
  return (
    String(row.status || "").toLowerCase() === "active" &&
    toMillis(row.endsAt) > Date.now()
  );
}

export default function StoreSponsorships() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [orders, setOrders] = useState([]);
  const [activeSponsors, setActiveSponsors] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [workingId, setWorkingId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const settingsRef = doc(db, "sponsorship_settings", "defaults");
    const unsubSettings = onSnapshot(
      settingsRef,
      (snap) => {
        setSettings({ ...DEFAULT_SETTINGS, ...(snap.data() || {}) });
      },
      (settingsError) => {
        console.error("sponsorship settings snapshot failed", settingsError);
        setError("Failed to load sponsorship settings.");
      },
    );

    const ordersQuery = query(
      collection(db, "store_sponsorship_orders"),
      orderBy("createdAt", "desc"),
    );
    const unsubOrders = onSnapshot(
      ordersQuery,
      (snap) => {
        setOrders(snap.docs.map((row) => ({ id: row.id, ...row.data() })));
      },
      (ordersError) => {
        console.error("store sponsorship orders snapshot failed", ordersError);
        setError("Failed to load sponsorship requests.");
      },
    );

    const activeQuery = query(
      collection(db, "store_sponsorships"),
      orderBy("updatedAt", "desc"),
    );
    const unsubSponsors = onSnapshot(
      activeQuery,
      (snap) => {
        setActiveSponsors(
          snap.docs
            .map((row) => ({ id: row.id, ...row.data() }))
            .filter(isActiveSponsor),
        );
      },
      (sponsorError) => {
        console.error("store sponsorships snapshot failed", sponsorError);
        setError("Failed to load active sponsored shops.");
      },
    );

    return () => {
      unsubSettings();
      unsubOrders();
      unsubSponsors();
    };
  }, []);

  const pendingOrders = useMemo(
    () =>
      orders.filter((row) =>
        ["pending", "submitted", "initiated"].includes(
          String(row.status || "").toLowerCase(),
        ),
      ),
    [orders],
  );

  async function saveSettings() {
    setSavingSettings(true);
    setMessage("");
    setError("");

    try {
      await setDoc(
        doc(db, "sponsorship_settings", "defaults"),
        {
          price: Number(settings.price || DEFAULT_SETTINGS.price),
          durationDays: Number(
            settings.durationDays || DEFAULT_SETTINGS.durationDays,
          ),
          maxSlots: Number(settings.maxSlots || DEFAULT_SETTINGS.maxSlots),
          note: String(settings.note || DEFAULT_SETTINGS.note).trim(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setMessage("Sponsorship settings saved.");
    } catch (saveError) {
      console.error(saveError);
      setError("Failed to save sponsorship settings.");
    } finally {
      setSavingSettings(false);
    }
  }

  async function approveOrder(order) {
    setWorkingId(order.id);
    setMessage("");
    setError("");

    try {
      const durationDays = Number(
        order.durationDays || settings.durationDays || DEFAULT_SETTINGS.durationDays,
      );
      const startsMs = Date.now();
      const endsMs = startsMs + durationDays * 24 * 60 * 60 * 1000;
      const startsAt = Timestamp.fromMillis(startsMs);
      const endsAt = Timestamp.fromMillis(endsMs);
      const storeId = order.storeId || order.instructorId;
      const publicDoc = {
        storeId,
        instructorUid: order.instructorId,
        orderId: order.id,
        status: "active",
        price: Number(order.price || settings.price || 0),
        durationDays,
        startsAt,
        endsAt,
        priority: Number(order.priority || 0),
        storeSnapshot: {
          displayName:
            order.storeSnapshot?.displayName ||
            order.storeSnapshot?.name ||
            order.instructorSnapshot?.name ||
            "Featured Shop",
          slug: order.storeSnapshot?.slug || "",
          profileImageUrl: order.storeSnapshot?.profileImageUrl || "",
          coverImageUrl: order.storeSnapshot?.coverImageUrl || "",
          categories: Array.isArray(order.storeSnapshot?.categories)
            ? order.storeSnapshot.categories
            : [],
          bio: order.storeSnapshot?.bio || "",
        },
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "store_sponsorships", storeId), publicDoc, {
        merge: true,
      });

      await updateDoc(doc(db, "store_sponsorship_orders", order.id), {
        status: "approved",
        startsAt,
        endsAt,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "stores", storeId),
        {
          sponsored: true,
          sponsorshipStatus: "active",
          sponsorshipStartsAt: startsAt,
          sponsorshipEndsAt: endsAt,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setMessage("Sponsored shop approved and published to the homepage.");
    } catch (approveError) {
      console.error(approveError);
      setError("Failed to approve this sponsorship request.");
    } finally {
      setWorkingId("");
    }
  }

  async function declineOrder(order) {
    setWorkingId(order.id);
    setMessage("");
    setError("");

    try {
      await updateDoc(doc(db, "store_sponsorship_orders", order.id), {
        status: "declined",
        declinedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setMessage("Sponsorship request declined.");
    } catch (declineError) {
      console.error(declineError);
      setError("Failed to decline this sponsorship request.");
    } finally {
      setWorkingId("");
    }
  }

  async function removeSponsor(row) {
    setWorkingId(row.id);
    setMessage("");
    setError("");

    try {
      await deleteDoc(doc(db, "store_sponsorships", row.id));

      await setDoc(
        doc(db, "stores", row.storeId || row.id),
        {
          sponsored: false,
          sponsorshipStatus: "inactive",
          sponsorshipEndsAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      if (row.orderId) {
        await setDoc(
          doc(db, "store_sponsorship_orders", row.orderId),
          {
            status: "expired",
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }

      setMessage("Sponsored placement removed.");
    } catch (removeError) {
      console.error(removeError);
      setError("Failed to remove this sponsored placement.");
    } finally {
      setWorkingId("");
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
              Store Promotion
            </div>
            <h1 className="mt-2 text-3xl font-black text-slate-900">
              Sponsored shops
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Set the sponsorship price and duration here, then approve
              instructor requests so their shops appear first on the luxury
              landing page.
            </p>
          </div>

          <div className="rounded-2xl border bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div>Pending requests: {pendingOrders.length}</div>
            <div>Live sponsored shops: {activeSponsors.length}</div>
          </div>
        </div>

        {(message || error) && (
          <div
            className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {error || message}
          </div>
        )}

        <div className="mt-6 grid gap-4 lg:grid-cols-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Sponsor price
            </label>
            <input
              type="number"
              min="0"
              value={settings.price ?? ""}
              onChange={(event) =>
                setSettings((current) => ({
                  ...current,
                  price: event.target.value,
                }))
              }
              className="w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Duration (days)
            </label>
            <input
              type="number"
              min="1"
              value={settings.durationDays ?? ""}
              onChange={(event) =>
                setSettings((current) => ({
                  ...current,
                  durationDays: event.target.value,
                }))
              }
              className="w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Max homepage slots
            </label>
            <input
              type="number"
              min="1"
              value={settings.maxSlots ?? ""}
              onChange={(event) =>
                setSettings((current) => ({
                  ...current,
                  maxSlots: event.target.value,
                }))
              }
              className="w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={saveSettings}
              disabled={savingSettings}
              className={`w-full rounded-xl px-5 py-3 font-semibold text-white ${
                savingSettings
                  ? "bg-blue-300"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Notes for instructors
          </label>
          <textarea
            rows={3}
            value={settings.note ?? ""}
            onChange={(event) =>
              setSettings((current) => ({ ...current, note: event.target.value }))
            }
            className="w-full rounded-2xl border bg-white px-4 py-3 outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <section className="rounded-3xl border bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              Pending sponsorship requests
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Approve these after confirming payment to publish their shops in
              the homepage spotlight.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {pendingOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              No pending sponsorship requests right now.
            </div>
          ) : (
            pendingOrders.map((order) => (
              <div
                key={order.id}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="text-xl font-bold text-slate-900">
                      {order.storeSnapshot?.displayName ||
                        order.instructorSnapshot?.name ||
                        "Store request"}
                    </div>
                    <div className="text-sm text-slate-600">
                      Instructor: {order.instructorSnapshot?.email || order.instructorId}
                    </div>
                    <div className="text-sm text-slate-600">
                      Price: {fmt(order.price || settings.price)} • Duration:{" "}
                      {Number(order.durationDays || settings.durationDays)} days
                    </div>
                    <div className="text-xs text-slate-500">
                      Submitted: {formatDate(order.createdAt)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {order.slipUrl ? (
                      <a
                        href={order.slipUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white"
                      >
                        Open slip
                      </a>
                    ) : null}
                    <button
                      type="button"
                      disabled={workingId === order.id}
                      onClick={() => approveOrder(order)}
                      className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {workingId === order.id ? "Working..." : "Approve"}
                    </button>
                    <button
                      type="button"
                      disabled={workingId === order.id}
                      onClick={() => declineOrder(order)}
                      className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-6 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Live sponsored placements
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            These shops are currently pinned into the homepage spotlight area.
          </p>
        </div>

        <div className="mt-5 space-y-4">
          {activeSponsors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              No active sponsored shops yet.
            </div>
          ) : (
            activeSponsors.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {row.storeSnapshot?.displayName || "Sponsored shop"}
                  </div>
                  <div className="text-sm text-slate-600">
                    Price: {fmt(row.price)} • Duration: {Number(row.durationDays || 0)} days
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Runs until {formatDate(row.endsAt)}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  {row.storeSnapshot?.slug ? (
                    <a
                      href={`/store/${row.storeSnapshot.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white"
                    >
                      Open store
                    </a>
                  ) : null}
                  <button
                    type="button"
                    disabled={workingId === row.id}
                    onClick={() => removeSponsor(row)}
                    className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                  >
                    {workingId === row.id ? "Removing..." : "Remove sponsor"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
